import { useEffect, useState } from 'react';
import { AuthClient } from '../../api/auth-client';
import { ApiError, needsStepUp } from '../../api/client';
import { Dialog, DialogActions } from '../../design-system/Overlay';
import type { HistoryPort, VersionRow } from './canvas-types';
import type { ChangeClass, Finding, GovernancePort } from './editor-types';
import { GovernancePanel } from './GovernancePanel';
import type { DiffLine } from './semantic-diff';

/**
 * Boîtes de la coquille des studios : palette de commandes (Ctrl K), vérification, proposition à quatre
 * yeux (circuit `ModelChange`), historique avec comparaison et restauration dans le brouillon. Boutons par
 * `DialogActions` : principale à droite, Annuler juste à gauche, X en haut. Aucun chemin ni JSON à l'écran.
 */

export interface PaletteCommand {
  id: string;
  label: string;
  keys?: string;
  run: () => void;
}

const normalize = (value: string): string => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('fr');
const messageOf = (error: unknown): string => (error instanceof ApiError ? error.message : error instanceof Error ? error.message : String(error));

export function CommandPalette({ open, commands, onClose }: { open: boolean; commands: readonly PaletteCommand[]; onClose: () => void }) {
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);
  const shown = commands.filter((command) => normalize(command.label).includes(normalize(query.trim())));
  const runOne = (command: PaletteCommand): void => {
    onClose();
    command.run();
  };
  return (
    <Dialog title="Commandes" open={open} onClose={onClose} size="list">
      <input
        type="search"
        aria-label="Chercher une commande"
        placeholder="Chercher une commande"
        autoFocus
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && shown[0] !== undefined) {
            event.preventDefault();
            runOne(shown[0]);
          }
        }}
      />
      <ul className="ay-studio-palette" aria-label="Commandes disponibles">
        {shown.map((command) => (
          <li key={command.id}>
            <button type="button" onClick={() => runOne(command)}>
              <span>{command.label}</span>
              {command.keys !== undefined && <kbd>{command.keys}</kbd>}
            </button>
          </li>
        ))}
        {shown.length === 0 && <li className="ay-studio__muted">Aucune commande ne correspond.</li>}
      </ul>
    </Dialog>
  );
}

function Findings({ findings, onLocate, empty }: { findings: readonly Finding[]; onLocate: (finding: Finding) => void; empty: string }) {
  if (findings.length === 0) return <p className="ay-studio__muted">{empty}</p>;
  return (
    <ul className="ay-findings">
      {findings.map((finding, index) => (
        <li key={`${finding.code}-${index}`} className={`ay-findings__item ay-findings__item--${finding.severity}`}>
          <button type="button" className="ay-findings__locate" onClick={() => onLocate(finding)}>
            <span className="ay-findings__severity">{finding.severity === 'error' ? 'À corriger' : 'À savoir'}</span>
            <span className="ay-findings__message">{finding.message}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Vérifier : les contrôles immédiats, puis le serveur (contrat, registre, recette, liaisons, règles). */
export function VerifyDialog<D>({ open, local, port, draft, onClose, onLocate }: { open: boolean; local: readonly Finding[]; port: GovernancePort<D>; draft: D; onClose: () => void; onLocate: (finding: Finding) => void }) {
  const [server, setServer] = useState<{ valid: boolean; findings: Finding[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setServer(null);
    setError(null);
    setBusy(true);
    port.validate(draft).then(
      (result) => alive && setServer(result),
      (failure: unknown) => alive && setError(messageOf(failure)),
    ).finally(() => alive && setBusy(false));
    return () => {
      alive = false;
    };
  }, [open, port, draft]);
  return (
    <Dialog title="Vérification du brouillon" open={open} onClose={onClose} size="md" footer={<DialogActions cancel={{ label: 'Fermer', onClick: onClose }} />}>
      <h3 className="ay-props__subtitle">Contrôles immédiats</h3>
      <Findings findings={local} onLocate={onLocate} empty="Rien à signaler." />
      <h3 className="ay-props__subtitle">Contrôle par le serveur</h3>
      {busy && <p className="ay-studio__muted" role="status">Vérification en cours…</p>}
      {error !== null && <p className="ay-field__error" role="alert">{error}</p>}
      {server !== null && (
        <>
          <p role="status" className={server.valid ? 'ay-governance__ok' : 'ay-governance__ko'}>{server.valid ? 'Le serveur accepte ce brouillon.' : 'Le serveur demande des corrections avant de proposer.'}</p>
          <Findings findings={server.findings} onLocate={onLocate} empty="Le serveur ne signale rien." />
        </>
      )}
    </Dialog>
  );
}

const STEPS = ['Brouillon', 'Validation et impact', 'Proposer', 'Décision par une autre personne'] as const;

/** Proposer : validation, impact, motif, puis décision par une seconde personne (jamais l'auteur). */
export function ProposeDialog<D>({ open, port, draft, estimate, localErrors, onClose, onPublished }: { open: boolean; port: GovernancePort<D>; draft: D; estimate: ChangeClass; localErrors: number; onClose: () => void; onPublished: () => void }) {
  return (
    <Dialog
      title="Proposer la publication"
      open={open}
      onClose={onClose}
      size="lg"
      note="Rien n’est publié sans l’accord d’une autre personne, en session élevée."
      footer={<DialogActions cancel={{ label: 'Fermer', onClick: onClose }} />}
    >
      <ol className="ay-studio-steps" aria-label="Étapes de la publication">{STEPS.map((step, index) => <li key={step}><span aria-hidden="true">{index + 1}</span>{step}</li>)}</ol>
      <GovernancePanel port={port} draft={draft} dirty={estimate.changes.length > 0} localErrors={localErrors} estimate={estimate} onLocate={() => onClose()} onPublished={() => onPublished()} />
    </Dialog>
  );
}

/** Historique : versions, comparaison au brouillon (ou au Pack d'origine), restauration dans le brouillon seulement. */
export function HistoryDialog<D>({ open, port, draft, onClose, onRestore }: { open: boolean; port: HistoryPort<D>; draft: D; onClose: () => void; onRestore: (document: D, row: VersionRow) => void }) {
  const [rows, setRows] = useState<VersionRow[] | null>(null);
  const [chosen, setChosen] = useState<{ row: VersionRow; document: D } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setChosen(null);
    setError(null);
    port.versions().then((found) => alive && setRows(found), (failure: unknown) => alive && setError(messageOf(failure)));
    return () => {
      alive = false;
    };
  }, [open, port]);
  const pick = (row: VersionRow): void => {
    setError(null);
    port.load(row).then((document) => setChosen({ row, document }), (failure: unknown) => setError(messageOf(failure)));
  };
  const comparison = chosen === null ? null : port.compare(chosen.document, draft);
  return (
    <Dialog
      title="Historique et comparaison"
      open={open}
      onClose={onClose}
      size="lg"
      note="Restaurer remet une version dans le brouillon ; rien n’est publié sans « Proposer » puis l’accord d’une autre personne."
      footer={<DialogActions cancel={{ label: 'Annuler', onClick: onClose }} primary={{ label: 'Restaurer dans le brouillon', disabled: chosen === null, onClick: () => chosen !== null && onRestore(chosen.document, chosen.row) }} />}
    >
      {error !== null && <p className="ay-field__error" role="alert">{error}</p>}
      <div className="ay-studio-history">
        <ol className="ay-studio-history__versions" aria-label="Versions">
          {rows === null && <li className="ay-studio__muted">Lecture de l’historique…</li>}
          {(rows ?? []).map((row) => (
            <li key={row.id}>
              <button type="button" aria-pressed={chosen?.row.id === row.id} className={`ay-studio-history__version is-${row.tone}`} onClick={() => pick(row)}>
                <span><strong>{row.name}</strong> <span className="ay-studio-history__tag">{row.tag}</span></span>
                <span className="ay-studio__muted">{row.meta}</span>
              </button>
            </li>
          ))}
        </ol>
        <section aria-label="Comparaison" className="ay-studio-history__diff">
          {comparison === null ? (
            <p className="ay-studio__muted">Choisissez une version pour la comparer au brouillon.</p>
          ) : (
            <>
              <p><strong>Comparer :</strong> {chosen!.row.name} → brouillon · {comparison.headline}</p>
              <ul className="ay-studio-diff">
                {comparison.lines.map((line: DiffLine, index) => (
                  <li key={index} className={`is-${line.sign === '+' ? 'added' : line.sign === '−' ? 'removed' : 'changed'}`}>
                    <span aria-hidden="true">{line.sign}</span>
                    <span className="ay-visually-hidden">{line.sign === '+' ? 'Ajouté : ' : line.sign === '−' ? 'Retiré : ' : 'Modifié : '}</span>
                    {line.text}
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>
      </div>
    </Dialog>
  );
}

/** Publier en un clic (la politique du tenant le permet) : le résumé des changements, un motif, puis la nouvelle version. */
export interface PublishWarningView {
  title: string;
  badge: string;
  lines: ReadonlyArray<{ label: string; detail: string }>;
  fallback: string;
  fix?: { label: string; detail?: string; disabledReason?: string } | undefined;
}

/**
 * Publier selon la politique du tenant (ADR-146 addendum (11)), avec, s'il y en a, les avertissements non
 * bloquants (langues incomplètes, addendum (14), planche « EditeurV2-Publier ») : « Publier quand même » (repli
 * sur la langue par défaut) ou l'action de rattrapage (« Traduire puis publier »).
 */
export function PublishDialog({ open, summary, warnings, changes, onClose, onPublish, stepUp = (code) => new AuthClient().stepUp(code).then(() => undefined) }: { open: boolean; summary: { headline: string; lines: readonly DiffLine[] } | null; warnings?: PublishWarningView | null | undefined; changes?: number | undefined; onClose: () => void; onPublish: (reason: string, fix: boolean) => Promise<string | null>; stepUp?: (code: string) => Promise<void> }) {
  const [reason, setReason] = useState('Publication depuis l’éditeur');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<'fallback' | 'fix'>('fallback');
  // Approbation refusée faute de vérification récente (politique `require_step_up`) : le code se saisit ici,
  // puis la publication reprend la proposition restée en attente.
  const [askCode, setAskCode] = useState(false);
  const [code, setCode] = useState('');
  useEffect(() => {
    if (open) {
      setError(null);
      setChoice('fallback');
      setAskCode(false);
      setCode('');
    }
  }, [open]);
  const publish = (): void => {
    setBusy(true);
    setError(null);
    const verified = askCode
      ? stepUp(code.trim()).catch((failure: unknown) => {
          throw failure instanceof ApiError && failure.code === 'NETWORK_UNAVAILABLE' ? failure : new Error('Code refusé : saisissez le code affiché maintenant.');
        })
      : Promise.resolve();
    verified
      .then(() => onPublish(reason.trim(), choice === 'fix'))
      .then(
        (message) => {
          setAskCode(false);
          setError(message);
        },
        (failure: unknown) => {
          if (needsStepUp(failure)) {
            setAskCode(true);
            setCode('');
            setError(askCode ? 'Code refusé ou expiré : saisissez le code affiché maintenant.' : null);
          } else setError(messageOf(failure));
        },
      )
      .finally(() => setBusy(false));
  };
  const fixDisabled = warnings?.fix?.disabledReason;
  return (
    <Dialog
      title="Publier la nouvelle version ?"
      open={open}
      onClose={onClose}
      size="md"
      note={changes === undefined ? 'Les tâches en cours gardent leur version.' : `${changes} modification${changes > 1 ? 's' : ''} · les tâches en cours gardent leur version.`}
      footer={<DialogActions cancel={{ label: 'Annuler', onClick: onClose }} primary={{ label: askCode ? 'Vérifier et publier' : warnings == null ? 'Publier' : 'Publier maintenant', busy, disabled: reason.trim().length < 3 || (askCode && code.trim().length < 6), onClick: publish }} />}
    >
      {summary !== null && (
        <>
          <p><strong>Ce qui change :</strong> {summary.headline}</p>
          <ul className="ay-studio-diff">
            {summary.lines.slice(0, 8).map((line, index) => (
              <li key={index} className={`is-${line.sign === '+' ? 'added' : line.sign === '−' ? 'removed' : 'changed'}`}>{line.text}</li>
            ))}
          </ul>
          {summary.lines.length > 8 && <p className="ay-studio__muted">… et {summary.lines.length - 8} autre(s).</p>}
        </>
      )}
      {warnings != null && (
        <section className="ay-studio-publish-warnings" aria-label={warnings.title}>
          <header>
            <strong>{warnings.title}</strong>
            <span className="ay-studio-pill">{warnings.badge}</span>
          </header>
          <ul>
            {warnings.lines.map((line) => (
              <li key={line.label}>
                <span>{line.label}</span>
                <span className="ay-studio__muted">{line.detail}</span>
              </li>
            ))}
          </ul>
          <div role="radiogroup" aria-label="Que faire des langues incomplètes" className="ay-studio-publish-warnings__choices">
            <button type="button" role="radio" aria-checked={choice === 'fallback'} onClick={() => setChoice('fallback')}>
              <strong>Publier quand même</strong>
              <span className="ay-studio__muted">{warnings.fallback}</span>
            </button>
            {warnings.fix !== undefined && (
              <button type="button" role="radio" aria-checked={choice === 'fix'} disabled={fixDisabled !== undefined} title={fixDisabled} onClick={() => setChoice('fix')}>
                <strong>{warnings.fix.label}</strong>
                {warnings.fix.detail !== undefined && <span className="ay-studio__muted">{warnings.fix.detail}</span>}
              </button>
            )}
          </div>
        </section>
      )}
      <label className="ay-field">
        <span className="ay-field__label">Motif de la version</span>
        <textarea rows={2} value={reason} onChange={(event) => setReason(event.target.value)} />
      </label>
      {askCode && (
        <>
          <label className="ay-field">
            <span className="ay-field__label">Code de vérification</span>
            <input inputMode="numeric" autoComplete="one-time-code" autoFocus value={code} onChange={(event) => setCode(event.target.value)} aria-describedby="ay-publish-step-up" />
          </label>
          <p id="ay-publish-step-up" className="ay-studio__muted">Code de votre application d’authentification.</p>
        </>
      )}
      {error !== null && <p className="ay-field__error" role="alert">{error}</p>}
    </Dialog>
  );
}
